import React from 'react'
import Box from '../../box';
import Typography from '../../typography';
import Card, { CardActions, CardHeader } from '../../card';
import Button from '../../button';
import Dialog, { DialogActions, DialogContent, DialogTitle } from '../../dialog';
import TextField from '../../textfield';
import dayjs, { Dayjs } from 'dayjs';
import DateTimePicker from '../../form/date_time_picker';
import MenuItem from '../../form/menu_item';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { AvailabilityExceptionItem } from './availability_step_context';

export interface AvailabilityExceptionListItemProps extends AvailabilityExceptionItem {
  onDelete: () => void
}

export const AvailabilityExceptionListItem: React.FC<AvailabilityExceptionListItemProps> = ({
  isAvailable,
  timeRange: {
    startDate,
    endDate
  },
  onDelete
}) => {
  return (
    <Card sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <CardHeader
        title={<Typography leftIcon={
          isAvailable ? <EventAvailableIcon color="success" /> : <EventBusyIcon color='warning' />
        } variant='h6'>{isAvailable ? 'Available' : 'Unavailable'}</Typography>}
        subheader={
          <Typography fontSize={14} fontWeight={'light'} variant='caption'>
            [{endDate.diff(startDate, 'hours')} hours]
            &nbsp;{startDate.toDate().toLocaleString()} - {endDate.toDate().toLocaleString()}
          </Typography>
        }
      />
      <CardActions>
        <Button variant='text' color='warning' onClick={onDelete}>Delete</Button>
      </CardActions>
    </Card>
  )
}

export interface AddExceptionButtonProps {
  onEventAdded: (event: AvailabilityExceptionItem) => void
}

export const AddExceptionButton: React.FC<AddExceptionButtonProps> = ({
  onEventAdded
}) => {
  const [isDialogOpen, setIsDialogOpen] = React.useState(false)
  const [isAvailable, setIsAvailable] = React.useState(false)
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null)
  const [startDate, setStartDate] = React.useState<Dayjs | null>()
  const [endDate, setEndDate] = React.useState<Dayjs | null>()

  const handleDialogClose = React.useCallback(() => {
    setIsDialogOpen(false)
  }, [])

  const handleDialogOpen = React.useCallback(() => {
    setIsDialogOpen(true)
  }, [])

  const clearFormData = React.useCallback(() => {
    setIsAvailable(false)
    setStartDate(null)
    setEndDate(null)
    setErrorMessage(null)
  }, [])

  const handleAddEvent = React.useCallback(() => {
    try {
      if (!startDate) throw new Error('No start date selected')
      if (!endDate) throw new Error('No end date selected')
      if (startDate.isAfter(endDate)) throw new Error('Start date must be before end date')
      onEventAdded({
        isAvailable,
        timeRange: {
          startDate,
          endDate
        }
      })
      clearFormData()
      handleDialogClose()
    } catch (error) {
      if (error instanceof Error) {
        setErrorMessage(error.message)
      } else {
        setErrorMessage('Something went wrong. Please try again later.')
      }
    }
  }, [startDate, endDate, onEventAdded, isAvailable, clearFormData, handleDialogClose])

  return (
    <>
      <Button variant='contained' color='secondary' onClick={handleDialogOpen}>Add Exception</Button>
      <Dialog open={isDialogOpen} onClose={handleDialogClose} sx={{
        width: '100%'
      }}>
        <DialogTitle>Add Exception</DialogTitle>
        <DialogContent>
          {errorMessage && <Box sx={{ color: 'error.main' }}>{errorMessage}</Box>}
          <TextField
            gutterBottom
            select
            fullWidth
            label={'Availability'}
            value={isAvailable}
            onChange={(e) => { setIsAvailable(e.target.value === 'true') }}
          >
            {/* <MenuItem value={'true'}>Available</MenuItem> */}
            <MenuItem value={'false'}>Unavailable</MenuItem>
          </TextField>
          <DateTimePicker
            minDate={dayjs().add(1, 'day')}
            maxDate={dayjs().add(3, 'month')}
            mobile
            gutterBottom
            fullWidth
            label={'Start Date'}
            value={startDate}
            onChange={setStartDate}
          />
          <DateTimePicker
            minDate={startDate ?? dayjs().add(1, 'day')}
            maxDate={startDate?.add(3, 'month')}
            gutterBottom
            mobile
            fullWidth
            label={'End Date'}
            value={endDate}
            onChange={setEndDate}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddEvent}>Add</Button>
          <Button onClick={handleDialogClose}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export interface AvailabilityExceptionProps {
  onChangeExceptions: (exceptions: AvailabilityExceptionItem[]) => void
  exceptions: AvailabilityExceptionItem[]
}

const AvailabilityExceptionFragment: React.FC<AvailabilityExceptionProps> = ({
  exceptions: events,
  onChangeExceptions
}) => {
  const handleDeleteEvent = React.useCallback((index: number) => {
    const newEvents = [...events]
    newEvents.splice(index, 1)
    onChangeExceptions(newEvents)
  }, [events, onChangeExceptions])

  const handleAddNewEvent = React.useCallback((event: AvailabilityExceptionItem) => {
    const newEvents = [...events, event].sort((a, b) =>
      a.timeRange.startDate.isBefore(b.timeRange.startDate) ? -1 : 1
    )
    onChangeExceptions(newEvents)
  }, [events, onChangeExceptions])

  return (
    <Box display={'flex'} flexDirection={'column'} gap={2}>
      <Box sx={{
        maxHeight: '400px',
        overflowY: 'auto'
      }}>
        <Box display={'flex'} flexDirection={'column'} gap={2}>
          {events.map((event, index) => (
            <AvailabilityExceptionListItem key={index} {...event} onDelete={() => { handleDeleteEvent(index); }} />
          ))}
        </Box>
      </Box>
      <AddExceptionButton onEventAdded={handleAddNewEvent} />
    </Box>
  )
}

export default AvailabilityExceptionFragment
